import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import { useForm } from 'react-hook-form';
import { 
  FiUpload, 
  FiImage, 
  FiFileText, 
  FiMapPin, 
  FiCalendar, 
  FiTag,
  FiX,
  FiCheck,
  FiCamera
} from 'react-icons/fi';
import Button from '../../components/UI/Button';

const UploadContainer = styled.div`
  min-height: 100vh;
  padding: var(--space-2xl) 0;
  background-color: var(--neutral-gray-50);
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 var(--space-lg);
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: var(--space-3xl);
  
  h1 {
    font-size: 2.5rem;
    color: var(--neutral-gray-900);
    margin-bottom: var(--space-md);
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }
  
  p {
    font-size: 1.125rem;
    color: var(--neutral-gray-600);
    max-width: 600px;
    margin: 0 auto;
  }
`;

const ContentTabs = styled.div`
  display: flex;
  background: white;
  border-radius: var(--radius-xl);
  padding: var(--space-sm);
  box-shadow: var(--shadow-md);
  margin-bottom: var(--space-2xl);
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: var(--space-sm);
  }
`;

const TabButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-sm);
  padding: var(--space-md) var(--space-lg);
  border: none;
  background: ${props => props.active ? 'var(--primary-green)' : 'transparent'};
  color: ${props => props.active ? 'white' : 'var(--neutral-gray-600)'};
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition: var(--transition-base);
  font-weight: 500;
  
  &:hover {
    background: ${props => props.active ? 'var(--primary-green-dark)' : 'var(--neutral-gray-100)'};
  }
`;

const UploadForm = styled.form`
  background: white;
  border-radius: var(--radius-2xl);
  padding: var(--space-2xl);
  box-shadow: var(--shadow-lg);
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-xl);
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const FormSection = styled.div`
  h3 {
    font-size: 1.25rem;
    color: var(--neutral-gray-900);
    margin-bottom: var(--space-lg);
    display: flex;
    align-items: center;
    gap: var(--space-sm);
  }
`;

const FormGroup = styled.div`
  margin-bottom: var(--space-lg);
  
  label {
    display: block;
    font-weight: 600;
    color: var(--neutral-gray-700);
    margin-bottom: var(--space-sm);
  }
  
  input, textarea, select {
    width: 100%;
    padding: var(--space-md);
    border: 1px solid var(--neutral-gray-300);
    border-radius: var(--radius-lg);
    font-size: 1rem;
    
    &:focus {
      border-color: var(--primary-green);
      box-shadow: 0 0 0 3px rgba(46, 139, 87, 0.1);
    }
  }
  
  textarea {
    min-height: 120px;
    resize: vertical;
  }
  
  .error {
    color: var(--accent-orange);
    font-size: 0.875rem;
    margin-top: var(--space-sm);
  }
`;

const DropzoneArea = styled.div`
  border: 2px dashed ${props => props.isDragActive ? 'var(--primary-green)' : 'var(--neutral-gray-300)'};
  border-radius: var(--radius-xl);
  padding: var(--space-3xl);
  text-align: center;
  background: ${props => props.isDragActive ? 'rgba(46, 139, 87, 0.05)' : 'var(--neutral-gray-50)'};
  cursor: pointer;
  transition: var(--transition-base);
  
  &:hover {
    border-color: var(--primary-green);
    background: rgba(46, 139, 87, 0.05);
  }
  
  .upload-icon {
    color: var(--primary-green);
    margin-bottom: var(--space-md);
  }
  
  h4 {
    color: var(--neutral-gray-900);
    margin-bottom: var(--space-sm);
  }
  
  p {
    color: var(--neutral-gray-600);
    margin-bottom: var(--space-md);
  }
  
  .file-types {
    font-size: 0.875rem;
    color: var(--neutral-gray-500);
  }
`;

const FilePreview = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: var(--space-md);
  margin-top: var(--space-lg);
`;

const FilePreviewItem = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: var(--radius-lg);
  overflow: hidden;
  background: var(--neutral-gray-100);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .file-info {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    padding: var(--space-sm);
    font-size: 0.75rem;
  }
  
  .remove-button {
    position: absolute;
    top: var(--space-sm);
    right: var(--space-sm);
    background: rgba(0, 0, 0, 0.7);
    color: white;
    border: none;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    
    &:hover {
      background: rgba(255, 0, 0, 0.7);
    }
  }
`;

const TagsInput = styled.div`
  .tags-container {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-sm);
    margin-top: var(--space-sm);
  }
  
  .tag {
    display: flex;
    align-items: center;
    gap: var(--space-xs);
    background: var(--primary-green);
    color: white;
    padding: var(--space-xs) var(--space-sm);
    border-radius: var(--radius-md);
    font-size: 0.875rem;
    
    button {
      background: none;
      border: none;
      color: white;
      cursor: pointer;
      padding: 0;
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: var(--space-md);
  justify-content: flex-end;
  margin-top: var(--space-2xl);
  padding-top: var(--space-xl);
  border-top: 1px solid var(--neutral-gray-200);
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const Upload = ({ user }) => {
  const [activeTab, setActiveTab] = useState('blog');
  const [files, setFiles] = useState([]);
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  const onDrop = useCallback((acceptedFiles) => {
    const newFiles = acceptedFiles.map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      preview: URL.createObjectURL(file)
    }));
    setFiles(prev => [...prev, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
      'video/*': ['.mp4', '.mov', '.avi']
    },
    multiple: true
  });

  const removeFile = (fileId) => {
    setFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const addTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags(prev => [...prev, newTag.trim()]);
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove) => {
    setTags(prev => prev.filter(tag => tag !== tagToRemove));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    
    try {
      if (activeTab === 'blog') {
        // Convert images to base64
        const imageData = await Promise.all(
          files.map(async (fileObj) => {
            const base64 = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.readAsDataURL(fileObj.file);
            });
            return {
              name: fileObj.file.name,
              data: base64,
              type: fileObj.file.type
            };
          })
        );

        // Submit blog post to API
        const response = await fetch('http://localhost:3001/api/submit-blog-post', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            title: data.title,
            content: data.description, // The form field is 'description'
            author: data.author,
            tags: tags,
            excerpt: data.description.substring(0, 200), // Auto-generate excerpt from first 200 chars
            images: imageData
          })
        });
        
        const result = await response.json();
        
        if (!response.ok) {
          throw new Error(result.error || 'Submission failed');
        }
        
        // Reset form
        reset();
        setFiles([]);
        setTags([]);
        
        // Show success message with PR link
        alert(`Blog post submitted successfully!\n\nYour submission is under review. Track progress at:\n${result.pullRequestUrl}`);
      } else {
        // For photos and park reviews (not yet implemented)
        alert(`${activeTab} submission coming soon! Currently only blog posts are available.`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert(`Upload failed: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contentTypes = [
    { id: 'blog', label: 'Blog Post', icon: <FiFileText size={20} /> },
    { id: 'photos', label: 'Photo Gallery', icon: <FiImage size={20} /> },
    { id: 'park', label: 'Park Review', icon: <FiMapPin size={20} /> }
  ];

  return (
    <UploadContainer>
      <Container>
        <PageHeader>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Upload Content
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Share your RV adventures, beautiful photos, and park reviews with our community
          </motion.p>
        </PageHeader>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <ContentTabs>
            {contentTypes.map(type => (
              <TabButton
                key={type.id}
                active={activeTab === type.id}
                onClick={() => setActiveTab(type.id)}
              >
                {type.icon}
                {type.label}
              </TabButton>
            ))}
          </ContentTabs>

          <UploadForm onSubmit={handleSubmit(onSubmit)}>
            <FormGrid>
              <FormSection>
                <h3>
                  <FiFileText size={20} />
                  Content Details
                </h3>
                
                <FormGroup>
                  <label htmlFor="author">Your Name *</label>
                  <input
                    id="author"
                    {...register('author', { required: 'Your name is required' })}
                    placeholder="Enter your name..."
                    defaultValue={user?.name || ''}
                  />
                  {errors.author && <div className="error">{errors.author.message}</div>}
                </FormGroup>

                <FormGroup>
                  <label htmlFor="title">Title *</label>
                  <input
                    id="title"
                    {...register('title', { required: 'Title is required' })}
                    placeholder="Enter a catchy title..."
                  />
                  {errors.title && <div className="error">{errors.title.message}</div>}
                </FormGroup>

                <FormGroup>
                  <label htmlFor="description">Description *</label>
                  <textarea
                    id="description"
                    {...register('description', { required: 'Description is required' })}
                    placeholder="Describe your experience, share tips, or tell a story..."
                  />
                  {errors.description && <div className="error">{errors.description.message}</div>}
                </FormGroup>

                {activeTab === 'park' && (
                  <>
                    <FormGroup>
                      <label htmlFor="parkName">Park Name *</label>
                      <input
                        id="parkName"
                        {...register('parkName', { required: 'Park name is required' })}
                        placeholder="e.g., Yellowstone National Park"
                      />
                      {errors.parkName && <div className="error">{errors.parkName.message}</div>}
                    </FormGroup>

                    <FormGroup>
                      <label htmlFor="rating">Rating</label>
                      <select id="rating" {...register('rating')}>
                        <option value="">Select a rating</option>
                        <option value="5">⭐⭐⭐⭐⭐ Excellent</option>
                        <option value="4">⭐⭐⭐⭐ Very Good</option>
                        <option value="3">⭐⭐⭐ Good</option>
                        <option value="2">⭐⭐ Fair</option>
                        <option value="1">⭐ Poor</option>
                      </select>
                    </FormGroup>
                  </>
                )}
              </FormSection>

              <FormSection>
                <h3>
                  <FiMapPin size={20} />
                  Location & Details
                </h3>

                <FormGroup>
                  <label htmlFor="location">Location</label>
                  <input
                    id="location"
                    {...register('location')}
                    placeholder="State, City, or specific location"
                  />
                </FormGroup>

                <FormGroup>
                  <label htmlFor="visitDate">Visit Date</label>
                  <input
                    id="visitDate"
                    type="date"
                    {...register('visitDate')}
                  />
                </FormGroup>

                <FormGroup>
                  <label htmlFor="category">Category</label>
                  <select id="category" {...register('category')}>
                    <option value="">Select a category</option>
                    <option value="national-park">National Park</option>
                    <option value="state-park">State Park</option>
                    <option value="campground">Campground</option>
                    <option value="scenic-drive">Scenic Drive</option>
                    <option value="city">City Visit</option>
                    <option value="events">Events & Festivals</option>
                    <option value="tips">RV Tips & Advice</option>
                  </select>
                </FormGroup>

                <FormGroup>
                  <label htmlFor="tags">Tags</label>
                  <input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Add tags and press Enter"
                  />
                  <TagsInput>
                    <div className="tags-container">
                      {tags.map(tag => (
                        <div key={tag} className="tag">
                          <FiTag size={12} />
                          {tag}
                          <button onClick={() => removeTag(tag)}>
                            <FiX size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </TagsInput>
                </FormGroup>
              </FormSection>
            </FormGrid>

            <FormSection style={{ marginTop: 'var(--space-xl)' }}>
              <h3>
                <FiCamera size={20} />
                Photos & Media
              </h3>

              <div {...getRootProps()}>
                <input {...getInputProps()} />
                <DropzoneArea isDragActive={isDragActive}>
                  <FiUpload size={48} className="upload-icon" />
                  <h4>Drag & drop your photos here</h4>
                  <p>or click to select files from your device</p>
                  <div className="file-types">
                    Supports: JPG, PNG, GIF, WebP, MP4, MOV (Max 10MB each)
                  </div>
                </DropzoneArea>
              </div>

              {files.length > 0 && (
                <FilePreview>
                  {files.map(file => (
                    <FilePreviewItem key={file.id}>
                      <img src={file.preview} alt="Preview" />
                      <div className="file-info">
                        {file.file.name}
                      </div>
                      <button
                        className="remove-button"
                        onClick={() => removeFile(file.id)}
                        type="button"
                      >
                        <FiX size={12} />
                      </button>
                    </FilePreviewItem>
                  ))}
                </FilePreview>
              )}
            </FormSection>

            <ActionButtons>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  reset();
                  setFiles([]);
                  setTags([]);
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  'Uploading...'
                ) : (
                  <>
                    <FiCheck size={16} />
                    Publish Content
                  </>
                )}
              </Button>
            </ActionButtons>
          </UploadForm>
        </motion.div>
      </Container>
    </UploadContainer>
  );
};

export default Upload;
